/*
 * © 2025 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConnectionTimeoutError = exports.GeneralSASTFailureError = exports.GeneralIACFailureError = exports.GeneralSCAFailureError = exports.ValidationFailureError = exports.TooManyVulnerablePathsError = exports.NoSupportedFilesFoundError = exports.CommandArgsError = exports.AuthConfigError = exports.GetVulnsFromResourceFailedError = exports.InvalidFlagOptionError = exports.EmptyFlagOptionError = exports.ConfigEnvironmentConsistencyIssueError = exports.ConfigEnvironmentFailedError = exports.GeneralCLIFailureError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name GeneralCLIFailureError
 * @description The encountered error only provides basic information, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands](https://docs.snyk.io/snyk-cli/commands)
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * @summary Unspecified Error
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralCLIFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unspecified Error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0000',
            status: 200,
            description: `The encountered error only provides basic information, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0000',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands',
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralCLIFailureError = GeneralCLIFailureError;
/**
 * @class
 * @name ConfigEnvironmentFailedError
 * @description The specified environment cannot be used. As a result, the configuration remains unchanged.Provide the correct specifications for the environment and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Unable to set environment
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to set environment',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0001',
            status: 200,
            description: `The specified environment cannot be used. As a result, the configuration remains unchanged.Provide the correct specifications for the environment and try again.`,
            errorCode: 'SNYK-CLI-0001',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentFailedError = ConfigEnvironmentFailedError;
/**
 * @class
 * @name ConfigEnvironmentConsistencyIssueError
 * @description You can configure the CLI in different ways, for example via Environment Variables or configuration file.
 * If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behavior.
 * Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Possible inconsistent configuration
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentConsistencyIssueError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Possible inconsistent configuration',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0002',
            status: 200,
            description: `You can configure the CLI in different ways, for example via Environment Variables or configuration file.
If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behavior.
Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.`,
            errorCode: 'SNYK-CLI-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentConsistencyIssueError = ConfigEnvironmentConsistencyIssueError;
/**
 * @class
 * @name EmptyFlagOptionError
 * @description A specified flag is missing an option value. Provide a correct option value and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Empty flag option
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class EmptyFlagOptionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Empty flag option',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0003',
            status: 200,
            description: `A specified flag is missing an option value. Provide a correct option value and try again.`,
            errorCode: 'SNYK-CLI-0003',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.EmptyFlagOptionError = EmptyFlagOptionError;
/**
 * @class
 * @name InvalidFlagOptionError
 * @description A specified flag option or combination is invalid. Provide a valid flag option or combination and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Invalid flag option
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class InvalidFlagOptionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Invalid flag option',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0004',
            status: 200,
            description: `A specified flag option or combination is invalid. Provide a valid flag option or combination and try again.`,
            errorCode: 'SNYK-CLI-0004',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.InvalidFlagOptionError = InvalidFlagOptionError;
/**
 * @class
 * @name GetVulnsFromResourceFailedError
 * @description If you are testing an npm package, check the version and package name and try running `snyk test` again. If you are testing a repository, try testing it at https://snyk.io/test/.For further assistance, run `snyk help` or see the Snyk docs.
 * @summary Unable to get vulnerabilities from resource
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GetVulnsFromResourceFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to get vulnerabilities from resource',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0005',
            status: 200,
            description: `If you are testing an npm package, check the version and package name and try running \`snyk test\` again. If you are testing a repository, try testing it at https://snyk.io/test/.For further assistance, run \`snyk help\` or see the Snyk docs.`,
            errorCode: 'SNYK-CLI-0005',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GetVulnsFromResourceFailedError = GetVulnsFromResourceFailedError;
/**
 * @class
 * @name AuthConfigError
 * @description When running your command, Snyk requires an authenticated account. You must include your API token as an environment value, or use `snyk auth` to authenticate.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli](https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli)
 * - [https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli](https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli)
 * @summary Missing AUTH token
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AuthConfigError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Missing AUTH token',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0006',
            status: 200,
            description: `When running your command, Snyk requires an authenticated account. You must include your API token as an environment value, or use \`snyk auth\` to authenticate.`,
            errorCode: 'SNYK-CLI-0006',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli',
                'https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AuthConfigError = AuthConfigError;
/**
 * @class
 * @name CommandArgsError
 * @description The specified CLI command includes missing or misconfigured arguments. Provide the correct arguments and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Incomplete command arguments
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class CommandArgsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Incomplete command arguments',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0007',
            status: 200,
            description: `The specified CLI command includes missing or misconfigured arguments. Provide the correct arguments and try again.`,
            errorCode: 'SNYK-CLI-0007',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.CommandArgsError = CommandArgsError;
/**
 * @class
 * @name NoSupportedFilesFoundError
 * @description Snyk could not detect any supported target files. Ensure the files you are importing are supported, that you are in the right directory, and try again.
 *
 * See more:
 * - [https://docs.snyk.io/supported-languages-package-managers-and-frameworks](https://docs.snyk.io/supported-languages-package-managers-and-frameworks)
 * @summary No supported files found
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NoSupportedFilesFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No supported files found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0008',
            status: 422,
            description: `Snyk could not detect any supported target files. Ensure the files you are importing are supported, that you are in the right directory, and try again.`,
            errorCode: 'SNYK-CLI-0008',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/supported-languages-package-managers-and-frameworks',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NoSupportedFilesFoundError = NoSupportedFilesFoundError;
/**
 * @class
 * @name TooManyVulnerablePathsError
 * @description There are too many vulnerable paths to process the project. If your command supports it, consider the following:pruning repeated sub-dependencies (`snyk test -p`); excluding directories (`snyk test --all-projects --exclude=dir1,file2`); setting a detection depth (`snyk test --all-projects --detection-depth=3`). If the error still occurs, consider debugging or contact Snyk Support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands)
 * - [https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p](https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p)
 * - [https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than](https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than)
 * - [https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than](https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than)
 * @summary Too many vulnerable paths to Project
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TooManyVulnerablePathsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Too many vulnerable paths to Project',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0009',
            status: 413,
            description: `There are too many vulnerable paths to process the project. If your command supports it, consider the following:pruning repeated sub-dependencies (\`snyk test -p\`); excluding directories (\`snyk test --all-projects --exclude=dir1,file2\`); setting a detection depth (\`snyk test --all-projects --detection-depth=3\`). If the error still occurs, consider debugging or contact Snyk Support.`,
            errorCode: 'SNYK-CLI-0009',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands',
                'https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p',
                'https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than',
                'https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TooManyVulnerablePathsError = TooManyVulnerablePathsError;
/**
 * @class
 * @name ValidationFailureError
 * @description CLI was unable to validate the required parameter. Provide the correct parameter and try again. If the error still occurs, consider debugging or contact Snyk Support.
 * @summary CLI validation failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ValidationFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'CLI validation failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0010',
            status: 400,
            description: `CLI was unable to validate the required parameter. Provide the correct parameter and try again. If the error still occurs, consider debugging or contact Snyk Support.`,
            errorCode: 'SNYK-CLI-0010',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ValidationFailureError = ValidationFailureError;
/**
 * @class
 * @name GeneralSCAFailureError
 * @description CLI was unable to execute your SCA command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/test](https://docs.snyk.io/snyk-cli/commands/test)
 * @summary SCA failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralSCAFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SCA failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0011',
            status: 200,
            description: `CLI was unable to execute your SCA command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0011',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/test',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralSCAFailureError = GeneralSCAFailureError;
/**
 * @class
 * @name GeneralIACFailureError
 * @description CLI was unable to execute your IAC command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/iac](https://docs.snyk.io/snyk-cli/commands/iac)
 * @summary IAC failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralIACFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'IAC failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0012',
            status: 200,
            description: `CLI was unable to execute your IAC command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0012',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/iac',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralIACFailureError = GeneralIACFailureError;
/**
 * @class
 * @name GeneralSASTFailureError
 * @description CLI was unable to execute your SAST command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/code](https://docs.snyk.io/snyk-cli/commands/code)
 * @summary SAST failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralSASTFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SAST failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0013',
            status: 200,
            description: `CLI was unable to execute your SAST command, please take a look at the given details.If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0013',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/code',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralSASTFailureError = GeneralSASTFailureError;
/**
 * @class
 * @name ConnectionTimeoutError
 * @description A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Request to Snyk API timeout
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConnectionTimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Request to Snyk API timeout',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-os-7001',
            status: 504,
            description: `A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.`,
            errorCode: 'SNYK-OS-7001',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConnectionTimeoutError = ConnectionTimeoutError;
//# sourceMappingURL=CLI-error-catalog.js.map